import {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
} from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Card,
  Col,
  ColProps,
  Form,
  Input,
  Row,
  notification,
} from 'antd';
import { DateTime } from 'luxon';
import { User } from '../../services/user-service/types';
import { Sprite } from '../sprite';
import StringUtils, { errorMessage as ErrorMessage } from '../../utils';
import { useEventStore } from '../../store/event-store';
import { Event } from '../../services/event-service/types';

/**
 * EditEventDetails props
 */
interface Props {}

interface EventForm {
  key?: string;
  required?: boolean;
  label?: string;
  colProps?: ColProps;
  render?: () => ReactElement;
  type?: string;
  placeholder?: string;
}

interface MultiCheckboxProps {
  options: string[];
  onChange: (values: string[]) => void;
  values: string[];
}

const MultiCheckbox = forwardRef<HTMLDivElement, MultiCheckboxProps>(
  ({ options, onChange, values }, ref) => {
    const [selectedOptions, setSelectedOptions] = useState<String[]>([]);

    useEffect(() => {
      setSelectedOptions(values || []);
    }, [values]);

    const handleCheckboxChange = option => {
      const updatedSelectedOptions = selectedOptions.includes(option)
        ? selectedOptions.filter(o => o !== option)
        : [...selectedOptions, option];

      setSelectedOptions(updatedSelectedOptions);
      onChange(updatedSelectedOptions); // Pass selected options to parent
    };

    return (
      <div
        ref={ref}
        className="p-4 bg-gray-200 border-slate-100 rounded-[10px] max-h-[250px] overflow-scroll">
        {options.map(option => (
          <p className="flex items-center space-x-2">
            <input
              type="checkbox"
              value={option}
              checked={
                values?.includes(option) || selectedOptions?.includes(option)
              }
              onChange={() => handleCheckboxChange(option)}
              className="form-checkbox"
            />
            &nbsp;{option}
          </p>
        ))}
      </div>
    );
  },
);

/**
 * EditEventDetails Component
 */
const EditEventDetails: FunctionComponent<Props> = () => {
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateEvent, createEvent, getCodes, getDetailedEvent, biomarkers } =
    useEventStore();

  const formFields = useMemo<EventForm[]>(
    () => [
      { key: 'name', placeholder: 'eg. Pri Med West' },
      { key: 'slug', placeholder: 'eg. pri-med-west' },
      { key: 'orderPrefix', placeholder: 'eg. PMW' },
      {
        key: 'testCode',
        label: 'Test Code',
        placeholder: '5 or 6 digits',
        maxlength: 6,
      },
      { key: 'clientId', label: 'Client ID' },
      { key: 'date', label: 'Event Date', type: 'date' },
      { key: 'biomarkers', label: 'Biomarkers', type: 'multi-checkbox' },
    ],
    [],
  );

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    control,
  } = useForm<Partial<Event>>({ defaultValues: {} });

  useEffect(() => {
    setIsLoading(true);
    const fetchCodes = async () => {
      try {
        await getCodes();
        if (id) {
          const event = await getDetailedEvent(Number(id));
          if (event && biomarkers) {
            // eslint-disable-next-line array-callback-return
            const keys = Object.keys(event);
            keys.map(e => {
              if (e === 'biomarkers') {
                const transformedBiomarkers = event[e].map(code => {
                  const biomarker = biomarkers.find(bio => bio.code === code);
                  return `${biomarker?.code} - ${biomarker?.name}`;
                });
                // console.log(e, transformedBiomarkers);
                setValue(e, transformedBiomarkers);
              } else {
                setValue(e as keyof Event, event[e]);
              }
              return null;
            });
          }
        }
      } catch (error) {
        console.error('Error fetching codes:', error);
      }
      setIsLoading(false);
    };
    fetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = handleSubmit(async data => {
    setIsLoading(true);
    try {
      if (id) {
        // eslint-disable-next-line no-param-reassign
        await updateEvent(Number(id), {
          ...data,
        });
        navigate(`/events/${id}`);
      } else {
        // eslint-disable-next-line no-param-reassign
        const id_ = await createEvent({
          ...data,
        });
        navigate(`/events/${id_}`);
      }
    } catch (err) {
      api.error({ message: ErrorMessage(err) });
    }
    setIsLoading(false);
  });

  const renderField = useCallback(
    (fieldForm: EventForm) => (
      <Controller
        control={control}
        name={fieldForm.key as keyof Event}
        rules={{
          required: fieldForm.required !== false,
          ...(fieldForm.key === 'testCode' && {
            pattern: {
              value: /^\d{5,6}$/,
              message: 'please enter 5 or 6 digits',
            },
          }),
        }}
        render={({ field: fieldProps }) => {
          if (fieldForm.render) {
            return fieldForm.render();
          }
          if (fieldForm.type === 'date') {
            const { onChange, value } = fieldProps;
            return (
              <Input
                type="date"
                className="h-[32px]"
                min={new Date().toISOString().split('T')[0]}
                value={
                  value
                    ? new Date(String(value)).toISOString().split('T')[0]
                    : ''
                }
                onChange={e => {
                  onChange({ target: { value: e.target.value } });
                }}
              />
            );
          }
          if (fieldForm.type === 'multi-checkbox') {
            const { onChange, value } = fieldProps;
            const options = biomarkers.map(e => `${e.code} - ${e.name}`).sort();
            return (
              <MultiCheckbox
                {...fieldProps}
                values={value as string[]}
                options={[...new Set(options)]}
                onChange={val => {
                  onChange({ target: { value: val } });
                }}
              />
            );
          }
          return (
            <Input
              {...fieldProps}
              value={fieldProps.value?.toString()}
              placeholder={fieldForm.placeholder}
            />
          );
        }}
      />
    ),
    [biomarkers, control],
  );

  const errorMessage = useCallback(
    (field: EventForm) => {
      if (field.key) {
        const error: FieldError | undefined = errors[field.key];
        if (error?.message) return error?.message;
        if (error?.type === 'required') {
          return 'this field is required.';
        }
        console.log(field);
        // pattern: '^d{5,6}$',

        return error?.type;
      }
      return 'missing field key';
    },
    [errors],
  );

  const creator = getValues('createdByUser') as User;
  const editor = getValues('updatedByUser') as User;

  return (
    <>
      {contextHolder}
      <Card
        title={
          <div className="py-5">
            <Row gutter={10}>
              <Col>
                <Avatar
                  icon={
                    <Sprite
                      name="icon-create"
                      className="text-blue fill-bue py-3 px-3"
                    />
                  }
                  className="bg-white border-slate-100"
                  size={50}
                  gap={1}
                />
              </Col>
              <Col>
                {id ? (
                  <>
                    <h4 className="text-2xl font-semibold mr-2">
                      {getValues('name')}
                    </h4>
                    <div className="text-xl pt-2.5">Edit Event No. {id}</div>
                  </>
                ) : (
                  <div className="text-xl pt-2.5">Create New Event</div>
                )}
              </Col>
            </Row>
          </div>
        }
        extra={
          <>
            <Button disabled={isLoading} onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              loading={isLoading}
              type="primary"
              className="ml-3 bg-red-900 text-white border-red-900"
              onClick={onSubmit}>
              {id ? `Save Event` : 'Create Event'}
            </Button>
          </>
        }>
        <Form layout="vertical">
          <Row gutter={20}>
            {formFields.map(field => (
              <Col key={field.key} span={8} {...(field.colProps || {})}>
                <div className="pb-1">
                  <Form.Item
                    label={
                      field.label ||
                      StringUtils.prettify(field.key as keyof Event)
                    }
                    name={field.key}
                    validateStatus={
                      errors[field.key as keyof Event] ? 'error' : undefined
                    }
                    help={errorMessage(field)}>
                    {renderField(field)}
                  </Form.Item>
                </div>
              </Col>
            ))}
          </Row>
          <Row gutter={20}>
            {creator && (
              <Col span={10}>
                <div className="pb-1">
                  <p>
                    Created By:{' '}
                    <strong>
                      {creator?.lastName || ''}, {creator?.firstName || ''}
                    </strong>{' '}
                    <br />
                    <small>
                      {DateTime.fromISO(getValues('createdAt') || '').toFormat(
                        'LL-dd-yyyy hh:mm a',
                      ) || ''}
                    </small>
                  </p>
                </div>
              </Col>
            )}
            <Col span={10}>
              {editor && (
                <div className="pb-1">
                  <p>
                    Updated By:{' '}
                    <strong>
                      {editor?.lastName || ''}, {editor?.firstName || ''}{' '}
                    </strong>
                    <br />
                    <small>
                      {DateTime.fromISO(getValues('updatedAt') || '').toFormat(
                        'LL-dd-yyyy hh:mm a',
                      ) || ''}
                    </small>
                  </p>
                </div>
              )}
            </Col>
          </Row>
        </Form>
      </Card>
    </>
  );
};

export default EditEventDetails;
